import React from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Typography,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Input,
  Box,
  Stack,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  ShoppingBag as ShoppingBagIcon,
  AttachMoney as AttachMoneyIcon,
  Straighten as StraightenIcon,
  LocalShipping as LocalShippingIcon,
  Scale as ScaleIcon
} from '@mui/icons-material';
import { CalculationParams, ProductCategory, ShippingMode } from '../types';

// 定义表单数据类型
interface FormData {
  purchasePrice: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  category: ProductCategory;
  shippingMode: ShippingMode;
  finalSellingPrice: number;
}

const schema = yup.object().shape({
  purchasePrice: yup.number()
    .typeError('请输入有效的数字')
    .required('请输入采购价')
    .positive('价格必须大于0'),
  weight: yup.number()
    .typeError('请输入有效的数字')
    .required('请输入重量')
    .positive('重量必须大于0'),
  length: yup.number()
    .typeError('请输入有效的数字')
    .required('请输入长度')
    .positive('长度必须大于0'),
  width: yup.number()
    .typeError('请输入宽度')
    .required('请输入宽度')
    .positive('宽度必须大于0'),
  height: yup.number()
    .typeError('请输入高度')
    .required('请输入高度')
    .positive('高度必须大于0'),
  category: yup.string()
    .oneOf(['pet', 'tool'] as const)
    .required('请选择商品类别'),
  shippingMode: yup.string()
    .oneOf(['standard', 'border'] as const)
    .required('请选择发货模式'),
  finalSellingPrice: yup.number()
    .typeError('请输入有效的数字')
    .required('请输入最终售价')
    .positive('价格必须大于0'),
}) as yup.ObjectSchema<FormData>;

interface CalculatorFormProps {
  onCalculate: (params: CalculationParams) => void;
  loading: boolean;
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({ onCalculate, loading }) => {
  const { 
    register, 
    handleSubmit, 
    control,
    reset,
    formState: { errors } 
  } = useForm<FormData>({
    defaultValues: {
      purchasePrice: 100,
      weight: 800,
      length: 10,
      width: 10,
      height: 10,
      category: 'tool',
      shippingMode: 'standard',
      finalSellingPrice: 200,
    },
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const { length, width, height, finalSellingPrice, ...rest } = data;
    const params: CalculationParams = {
      ...rest,
      dimensions: { length, width, height },
      finalSellingPrice,
    };
    onCalculate(params);
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 4, 
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: (theme) => theme.shadows[2]
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3, 
          pb: 1.5, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
          fontWeight: 600
        }}
      >
        <ShoppingBagIcon />
        商品信息
      </Typography>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3.5}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* 价格信息 - 一行两列 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  {/* 采购价 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>采购价 (¥)</InputLabel>
                      <Input
                        {...register('purchasePrice', { valueAsNumber: true })}
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        error={!!errors.purchasePrice}
                        data-testid="purchase-price-input"
                        startAdornment={
                          <InputAdornment position="start">¥</InputAdornment>
                        }
                      />
                      {errors.purchasePrice && (
                        <FormHelperText error>{errors.purchasePrice.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                  
                  {/* 最终售价 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>最终售价 (¥)</InputLabel>
                      <Input
                        {...register('finalSellingPrice', { valueAsNumber: true })}
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        error={!!errors.finalSellingPrice}
                        data-testid="final-selling-price-input"
                        startAdornment={
                          <InputAdornment position="start">¥</InputAdornment>
                        }
                      />
                      {errors.finalSellingPrice && (
                        <FormHelperText error>{errors.finalSellingPrice.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                {/* 尺寸重量信息 - 一行四列 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 3 }}>
                  {/* 长度 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>长度 (cm)</InputLabel>
                      <Input
                        {...register('length', { valueAsNumber: true })}
                        type="number"
                        inputProps={{ min: 0, step: 0.1 }}
                        error={!!errors.length}
                        data-testid="length-input"
                        endAdornment={
                          <InputAdornment position="end">cm</InputAdornment>
                        }
                      />
                      {errors.length && (
                        <FormHelperText error>{errors.length.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* 宽度 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>宽度 (cm)</InputLabel>
                      <Input
                        {...register('width', { valueAsNumber: true })}
                        type="number"
                        inputProps={{ min: 0, step: 0.1 }}
                        error={!!errors.width}
                        data-testid="width-input"
                        endAdornment={
                          <InputAdornment position="end">cm</InputAdornment>
                        }
                      />
                      {errors.width && (
                        <FormHelperText error>{errors.width.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* 高度 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>高度 (cm)</InputLabel>
                      <Input
                        {...register('height', { valueAsNumber: true })}
                        type="number"
                        inputProps={{ min: 0, step: 0.1 }}
                        error={!!errors.height}
                        data-testid="height-input"
                        endAdornment={
                          <InputAdornment position="end">cm</InputAdornment>
                        }
                      />
                      {errors.height && (
                        <FormHelperText error>{errors.height.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* 重量 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>重量 (g)</InputLabel>
                      <Input
                        {...register('weight', { valueAsNumber: true })}
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        error={!!errors.weight}
                        data-testid="weight-input"
                        startAdornment={
                          <InputAdornment position="start">
                            <ScaleIcon fontSize="small" />
                          </InputAdornment>
                        }
                        endAdornment={
                          <InputAdornment position="end">g</InputAdornment>
                        }
                      />
                      {errors.weight && (
                        <FormHelperText error>{errors.weight.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                {/* 商品类别和发货模式 - 一行两列 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  {/* 商品类别 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>商品类别</InputLabel>
                      <Controller
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            error={!!errors.category}
                            disabled={loading}
                            label="商品类别"
                            data-testid="category-select"
                          >
                            <MenuItem value="pet">宠物用品</MenuItem>
                            <MenuItem value="tool">工具</MenuItem>
                          </Select>
                        )}
                      />
                      {errors.category && (
                        <FormHelperText error>{errors.category.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* 发货模式 */}
                  <Box>
                    <FormControl fullWidth>
                      <InputLabel>发货模式</InputLabel>
                      <Controller
                        name="shippingMode"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            error={!!errors.shippingMode}
                            disabled={loading}
                            label="发货模式"
                            data-testid="shipping-mode-select"
                          >
                            <MenuItem value="standard">自发货</MenuItem>
                            <MenuItem value="border">边境仓</MenuItem>
                          </Select>
                        )}
                      />
                      {errors.shippingMode && (
                        <FormHelperText error>{errors.shippingMode.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 按钮组 */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', gap: 2, p: { xs: 2, sm: 3 } }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ 
                  py: 1.5,
                  fontWeight: 500,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 3 }
                }}
              >
                {loading ? '计算中...' : '计算'}
              </Button>
              
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                fullWidth
                size="large"
                data-testid="reset-button"
                sx={{ py: 1.5, fontWeight: 500 }}
                onClick={() => {
                  // Reset form to default values
                  reset({
                    purchasePrice: 100,
                    weight: 800,
                    length: 10,
                    width: 10,
                    height: 10,
                    category: 'tool',
                    shippingMode: 'standard',
                    finalSellingPrice: 200,
                  });
                }}
              >
                重置
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </form>
    </Paper>
  );
};

export default CalculatorForm;
